import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { StatCard, Loading, EmptyState, StatusBadge } from "@/components/common";
import PeriodFilter, { usePeriod, PERIOD_SUFFIX } from "@/components/PeriodFilter";
import { rupiah, numberID, formatDate } from "@/lib/format";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  FileText, FileClock, FileCheck2, ClipboardList, PackageCheck, PackageX,
  Clock, Truck, ShoppingBag,
} from "lucide-react";

export default function PurchasingDashboard() {
  const { period, start_date, end_date, setPeriod } = usePeriod();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const params = { period };
  if (period === "custom") { params.start_date = start_date; params.end_date = end_date; }
  const key = JSON.stringify(params);
  const suf = PERIOD_SUFFIX[period] || "";

  useEffect(() => {
    if (period === "custom" && (!start_date || !end_date)) { setLoading(false); return; }
    setLoading(true);
    api.get("/dashboard/purchasing", { params }).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  const k = data?.kpi;

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="pur-dash-title">Dashboard Purchasing</h1>
          <p className="text-sm text-slate-500 mt-1">Status permintaan pembelian, purchase order, dan penerimaan barang.</p>
        </div>
        <PeriodFilter period={period} start_date={start_date} end_date={end_date} onChange={setPeriod} />
      </div>

      {loading ? <Loading label="Memuat dashboard purchasing..." /> : !k ? <EmptyState title="Data tidak tersedia" /> : (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            <StatCard label={`PR Draft (${suf})`} value={numberID(k.pr_draft)} icon={FileText} accent="slate" testId="pu-pr-draft" />
            <StatCard label={`PR Menunggu Approval (${suf})`} value={numberID(k.pr_pending)} icon={FileClock} accent="amber" testId="pu-pr-pending" />
            <StatCard label={`PR Disetujui (${suf})`} value={numberID(k.pr_approved)} icon={FileCheck2} accent="emerald" testId="pu-pr-approved" />
            <StatCard label={`PO Aktif (${suf})`} value={numberID(k.po_active)} icon={ClipboardList} accent="indigo" testId="pu-po-active" />
            <StatCard label={`PO Sebagian Diterima (${suf})`} value={numberID(k.po_partial)} icon={PackageCheck} accent="sky" testId="pu-po-partial" />
            <StatCard label={`PO Belum Diterima (${suf})`} value={numberID(k.po_not_received)} icon={PackageX} accent="amber" testId="pu-po-notrecv" />
            <StatCard label="PO Terlambat" value={numberID(k.po_overdue)} icon={Clock} accent="rose" hint="Snapshot saat ini" testId="pu-po-overdue" />
            <StatCard label={`Penerimaan (${suf})`} value={numberID(k.gr_period)} icon={Truck} accent="emerald" testId="pu-gr-period" />
            <StatCard label="Supplier Aktif" value={numberID(k.suppliers_active)} icon={ShoppingBag} accent="slate" hint="Snapshot saat ini" testId="pu-suppliers" />
          </div>

          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
            <RecentBlock title="PR Terbaru" empty="Belum ada PR">
              {data.recent_pr.map((p) => (
                <Row key={p.pr_no} no={p.pr_no} sub={p.warehouse_name} status={p.status} right={rupiah(p.est_grand_total)} date={p.tanggal} testId={`recent-pr-${p.pr_no}`} />
              ))}
            </RecentBlock>
            <RecentBlock title="PO Terbaru" empty="Belum ada PO">
              {data.recent_po.map((p) => (
                <Row key={p.po_no} no={p.po_no} sub={p.supplier_name} status={p.status} right={rupiah(p.grand_total)} date={p.tanggal} testId={`recent-po-${p.po_no}`} />
              ))}
            </RecentBlock>
            <RecentBlock title="Penerimaan Terbaru" empty="Belum ada penerimaan">
              {data.recent_gr.map((g) => (
                <Row key={g.gr_no} no={g.gr_no} sub={`${g.supplier_name || ""} · ${g.po_no || ""}`} date={g.tanggal} testId={`recent-gr-${g.gr_no}`} />
              ))}
            </RecentBlock>
          </div>
        </>
      )}
    </div>
  );
}

function RecentBlock({ title, empty, children }) {
  const arr = React.Children.toArray(children);
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 px-4 py-3"><p className="font-display text-sm font-semibold text-slate-800">{title}</p></div>
      <div className="divide-y divide-slate-50">
        {arr.length === 0 ? <p className="px-4 py-8 text-center text-sm text-slate-400">{empty}</p> : arr}
      </div>
    </div>
  );
}

function Row({ no, sub, status, right, date, testId }) {
  return (
    <div className="flex items-center gap-3 px-4 py-2.5" data-testid={testId}>
      <div className="min-w-0 flex-1">
        <p className="font-mono text-xs font-semibold text-slate-800">{no}</p>
        <p className="truncate text-[11px] text-slate-400">{sub}{date ? ` · ${formatDate(date)}` : ""}</p>
      </div>
      {status && <StatusBadge status={status} />}
      {right && <span className="text-xs font-medium text-slate-600">{right}</span>}
    </div>
  );
}
